import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getPartnerPortalDashboard } from "../../api/partnerPortalApi";
import { usePartnerAuth } from "../../auth/PartnerAuthProvider";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { casamapaAgencyInventoryPath } from "../../constants/casamapaAgencyPortal";
import {
  CASAMAPA_PARTNER_FREE_TOOL_KEYS,
  CASAMAPA_PARTNER_FREE_TOOLS,
} from "../../constants/casamapaPartnerFreeTools";
import { useLocale, useCurrency } from "../../intl";
import { isCasaMapaVendorPartner } from "../../utils/casamapaPartnerPortalTabs";
import "../../styles/partnerPortalDashboard.css";
import "../../styles/partnerCasaMapaDashboard.css";
import "../../styles/partnerCasaMapaTools.css";

function greetingEmoji() {
  const hour = new Date().getHours();
  if (hour < 12) return "☀️";
  if (hour < 18) return "🌤️";
  return "🌙";
}

function firstName(partner) {
  const raw = partner?.firstName || partner?.displayName || partner?.businessName || "Partner";
  return String(raw).split(" ")[0];
}

function formatPriceRange(agency, formatMoney) {
  if (agency?.priceMinCop == null && agency?.priceMaxCop == null) return "—";
  const min = agency.priceMinCop != null ? formatMoney(agency.priceMinCop) : "—";
  const max = agency.priceMaxCop != null ? formatMoney(agency.priceMaxCop) : "—";
  if (min === max) return min;
  return `${min} – ${max}`;
}

function DashboardSkeleton() {
  return (
    <div className="cm-dashboard cm-dashboard--loading" aria-busy="true">
      <div className="partner-skeleton-block partner-skeleton-hero" />
      <div className="partner-kpi-grid">
        {[1, 2, 3, 4].map((id) => (
          <div key={id} className="partner-skeleton-block partner-skeleton-kpi" />
        ))}
      </div>
      <div className="cm-dashboard__skeleton-main">
        <div className="partner-skeleton-block cm-dashboard__skeleton-panel" />
        <div className="partner-skeleton-block cm-dashboard__skeleton-panel" />
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, hint, tone = "slate" }) {
  return (
    <article className={`partner-kpi-card partner-kpi-card--${tone}`}>
      <span className="partner-kpi-card__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="partner-kpi-card__label">{label}</span>
      <strong className="partner-kpi-card__value">{value ?? "—"}</strong>
      {hint ? <small className="partner-kpi-card__hint">{hint}</small> : null}
    </article>
  );
}

function PipelineBar({ label, value, max, tone = "blue" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="cm-dashboard__pipeline-row">
      <div className="cm-dashboard__pipeline-head">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div className="cm-dashboard__pipeline-track" aria-hidden="true">
        <div
          className={`cm-dashboard__pipeline-fill cm-dashboard__pipeline-fill--${tone}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function AgencyWorkspaceCard({ agency, t, formatMoney }) {
  const saleCount = agency.saleListingCount ?? 0;
  const rentCount = agency.rentListingCount ?? 0;
  const mixTotal = saleCount + rentCount;
  const salePct = mixTotal > 0 ? Math.round((saleCount / mixTotal) * 100) : 50;
  const inventoryPath = casamapaAgencyInventoryPath(agency.slug);

  return (
    <article className="cm-dashboard__agency-row cm-partner-agency-card">
      <div className="cm-dashboard__agency-row-head">
        {agency.logoUrl ? (
          <img src={agency.logoUrl} alt="" className="cm-dashboard__agency-logo" />
        ) : (
          <span className="cm-dashboard__agency-mark" aria-hidden="true">
            {String(agency.name || "?").slice(0, 1)}
          </span>
        )}
        <div className="cm-dashboard__agency-copy">
          <h3>{agency.name}</h3>
          <p>
            {(agency.activeListingCount ?? 0).toLocaleString()}{" "}
            {t("realEstate.casaMapa.admin.agencies.activeListings").toLowerCase()}
            {agency.priceMinCop != null || agency.priceMaxCop != null
              ? ` · ${formatPriceRange(agency, formatMoney)}`
              : ""}
          </p>
        </div>
        <div className="cm-dashboard__agency-actions">
          <Link className="cm-dashboard__agency-btn" to={inventoryPath}>
            {t("realEstate.casaMapa.admin.agencies.reviewInventory")}
          </Link>
        </div>
      </div>
      {mixTotal > 0 ? (
        <div className="cm-dashboard__mix" aria-hidden="true">
          <div className="cm-dashboard__mix-labels">
            <span>
              {t("realEstate.casaMapa.admin.listingType.sale")} {salePct}%
            </span>
            <span>
              {t("realEstate.casaMapa.admin.listingType.rent")} {100 - salePct}%
            </span>
          </div>
          <div className="cm-dashboard__mix-track">
            <div className="cm-dashboard__mix-sale" style={{ width: `${salePct}%` }} />
            <div className="cm-dashboard__mix-rent" style={{ width: `${100 - salePct}%` }} />
          </div>
        </div>
      ) : null}
    </article>
  );
}

export default function CasaMapaPartnerDashboardPage() {
  const { partner } = usePartnerAuth();
  const { path, portalLabel } = usePartnerPortalBase();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const isVendorPartner = isCasaMapaVendorPartner(partner);
  const businessName = partner?.businessName || partner?.displayName;

  useEffect(() => {
    let cancelled = false;

    getPartnerPortalDashboard()
      .then((dashboard) => {
        if (!cancelled) setData(dashboard);
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(error.message || t("realEstate.casaMapa.admin.errors.loadDashboard"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  const summary = data?.summary;
  const inventory = data?.casamapaInventory;
  const performance = data?.casamapaPerformance;
  const performanceTotals = performance?.totals;
  const agencies = inventory?.agencies || inventory?.topAgencies || [];
  const vendorProfiles = inventory?.vendorProfiles?.vendors || [];
  const draftVendorCount =
    inventory?.vendorProfiles?.draftCount ??
    vendorProfiles.filter((vendor) => vendor.publicationStatus === "draft").length;
  const publishedVendorCount =
    inventory?.vendorProfiles?.publishedCount ??
    vendorProfiles.filter((vendor) => vendor.publicationStatus === "published").length;
  const primaryAgency = agencies.length === 1 ? agencies[0] : null;
  const primaryInventoryPath = primaryAgency
    ? casamapaAgencyInventoryPath(primaryAgency.slug)
    : null;

  const attentionItems = useMemo(() => {
    if (!summary) return [];
    const items = [];

    if (summary.newLeads > 0) {
      items.push({
        label: t("realEstate.casaMapa.admin.partnerDashboard.attention.newLeads", {
          count: summary.newLeads,
        }),
        to: path("leads"),
        action: t("realEstate.casaMapa.admin.partnerDashboard.actions.review"),
        tone: "urgent",
      });
    }

    if (summary.correctionsPending > 0) {
      items.push({
        label: t("realEstate.casaMapa.admin.partnerDashboard.attention.corrections", {
          count: summary.correctionsPending,
        }),
        to: path("listing-corrections"),
        action: t("realEstate.casaMapa.admin.partnerDashboard.actions.open"),
        tone: "warn",
      });
    }

    if (summary.profileCompleteness < 100) {
      items.push({
        label: t("realEstate.casaMapa.admin.partnerDashboard.attention.profile"),
        to: path("profile"),
        action: t("realEstate.casaMapa.admin.partnerDashboard.actions.finish"),
        tone: "neutral",
      });
    }

    if (!agencies.length && !isVendorPartner) {
      items.push({
        label: t("realEstate.casaMapa.admin.partnerDashboard.attention.noAgencies"),
        to: path("agencies"),
        action: t("realEstate.casaMapa.admin.partnerDashboard.actions.browse"),
        tone: "neutral",
      });
    }

    if (isVendorPartner && draftVendorCount > 0) {
      items.push({
        label: t("realEstate.casaMapa.admin.partnerDashboard.attention.draftVendor", {
          count: draftVendorCount,
        }),
        to: path("directory"),
        action: t("realEstate.casaMapa.admin.partnerDashboard.actions.open"),
        tone: "warn",
      });
    }

    if (isVendorPartner && publishedVendorCount === 0 && vendorProfiles.length > 0) {
      items.push({
        label: t("realEstate.casaMapa.admin.partnerDashboard.attention.unpublishedVendor"),
        to: path("directory"),
        action: t("realEstate.casaMapa.admin.partnerDashboard.actions.finish"),
        tone: "neutral",
      });
    }

    return items;
  }, [agencies.length, draftVendorCount, isVendorPartner, path, publishedVendorCount, summary, t, vendorProfiles.length]);

  const salePct = useMemo(() => {
    const total = (inventory?.saleListings ?? 0) + (inventory?.rentListings ?? 0);
    if (!total) return 50;
    return Math.round(((inventory?.saleListings ?? 0) / total) * 100);
  }, [inventory?.rentListings, inventory?.saleListings]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (errorMessage) {
    return (
      <div className="cm-dashboard">
        <p className="admin-data-health-error">{errorMessage}</p>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const leadMax = Math.max(summary.assignedLeads, summary.newLeads, 1);

  return (
    <div className="cm-dashboard">
      {partner?.accountStatus === "pending" ? (
        <aside className="cm-dashboard__review-banner" role="status">
          <strong>{t("realEstate.casaMapa.admin.partnerDashboard.reviewBanner.title")}</strong>
          <p>{t("realEstate.casaMapa.admin.partnerDashboard.reviewBanner.body")}</p>
        </aside>
      ) : null}
      <header className="cm-dashboard__hero partner-dashboard-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">
            {greetingEmoji()} {portalLabel}
          </p>
          <h2>
            {t("realEstate.casaMapa.admin.partnerDashboard.welcome", {
              name: firstName(partner),
            })}
          </h2>
          <p className="partner-dashboard-hero__intro">
            {isVendorPartner
              ? businessName
                ? t("realEstate.casaMapa.admin.partnerDashboard.vendorSubtitleWithBusiness", {
                    business: businessName,
                  })
                : t("realEstate.casaMapa.admin.partnerDashboard.vendorSubtitle")
              : businessName
                ? t("realEstate.casaMapa.admin.partnerDashboard.subtitleWithBusiness", {
                    business: businessName,
                  })
                : t("realEstate.casaMapa.admin.partnerDashboard.subtitle")}
          </p>
        </div>
        <div className="partner-dashboard-hero__actions">
          {isVendorPartner ? (
            <>
              <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("directory")}>
                ⌖ {t("realEstate.casaMapa.admin.partnerDashboard.viewDirectory")}
              </Link>
              <Link className="partner-dashboard-btn" to={path("leads")}>
                📬 {t("realEstate.casaMapa.admin.nav.leads")}
              </Link>
              <Link className="partner-dashboard-btn" to={path("market")}>
                ◐ {t("realEstate.casaMapa.admin.nav.market")}
              </Link>
            </>
          ) : primaryInventoryPath ? (
            <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={primaryInventoryPath}>
              🏢 {t("realEstate.casaMapa.admin.agencies.reviewInventory")}
            </Link>
          ) : agencies.length > 1 ? (
            <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("agencies")}>
              🏢 {t("realEstate.casaMapa.admin.partnerDashboard.yourAgencies")}
            </Link>
          ) : (
            <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("profile")}>
              👤 {t("realEstate.casaMapa.admin.nav.profile")}
            </Link>
          )}
          <Link className="partner-dashboard-btn" to={path("leads")}>
            📬 {t("realEstate.casaMapa.admin.nav.leads")}
          </Link>
          <Link className="partner-dashboard-btn" to={path("profile")}>
            ⚙️ {t("realEstate.casaMapa.admin.nav.profile")}
          </Link>
        </div>
      </header>

      <section className="partner-quick-grid" aria-label={t("realEstate.casaMapa.admin.dashboard.quickActions")}>
        {isVendorPartner ? (
          <>
            <Link className="partner-quick-card" to={path("directory")}>
              <span className="partner-quick-card__emoji">⌖</span>
              <strong>{t("realEstate.casaMapa.admin.partnerDashboard.viewDirectory")}</strong>
              <span>{t("realEstate.casaMapa.admin.partnerDashboard.directoryHint")}</span>
            </Link>
            <Link className="partner-quick-card" to={path("leads")}>
              <span className="partner-quick-card__emoji">📥</span>
              <strong>{t("realEstate.casaMapa.admin.nav.leads")}</strong>
              <span>{t("realEstate.casaMapa.admin.partnerDashboard.leadsHint")}</span>
            </Link>
            <Link className="partner-quick-card" to={path("market")}>
              <span className="partner-quick-card__emoji">◐</span>
              <strong>{t("realEstate.casaMapa.admin.nav.market")}</strong>
              <span>{t("realEstate.casaMapa.admin.partnerDashboard.toolsSectionHint")}</span>
            </Link>
            <Link className="partner-quick-card" to={path("profile")}>
              <span className="partner-quick-card__emoji">✨</span>
              <strong>{t("realEstate.casaMapa.admin.dashboard.profileHealth")}</strong>
              <span>
                {t("realEstate.casaMapa.admin.partnerDashboard.kpi.profileHint")} · {summary.profileCompleteness}%
              </span>
            </Link>
          </>
        ) : (
          <>
        {agencies.length ? (
          <Link className="partner-quick-card" to={primaryInventoryPath || path("agencies")}>
            <span className="partner-quick-card__emoji">🏢</span>
            <strong>{t("realEstate.casaMapa.admin.partnerDashboard.yourAgencies")}</strong>
            <span>
              {inventory
                ? t("realEstate.casaMapa.admin.dashboard.agencyQuickStat", {
                    count: inventory.agencyCount,
                    listings: (inventory.totalListings ?? 0).toLocaleString(),
                  })
                : t("realEstate.casaMapa.admin.partnerDashboard.agenciesHint")}
            </span>
          </Link>
        ) : (
          <Link className="partner-quick-card" to={path("profile")}>
            <span className="partner-quick-card__emoji">👤</span>
            <strong>{t("realEstate.casaMapa.admin.nav.profile")}</strong>
            <span>{t("realEstate.casaMapa.admin.partnerDashboard.profileHint")}</span>
          </Link>
        )}
        <Link className="partner-quick-card" to={path("listings")}>
          <span className="partner-quick-card__emoji">🏘️</span>
          <strong>{t("realEstate.casaMapa.admin.nav.listings")}</strong>
          <span>
            {inventory
              ? t("realEstate.casaMapa.admin.dashboard.listingsQuickStat", {
                  sale: inventory.saleListings ?? 0,
                  rent: inventory.rentListings ?? 0,
                })
              : t("realEstate.casaMapa.admin.partnerDashboard.listingsHint")}
          </span>
        </Link>
        <Link className="partner-quick-card" to={path("leads")}>
          <span className="partner-quick-card__emoji">📥</span>
          <strong>{t("realEstate.casaMapa.admin.nav.leads")}</strong>
          <span>{t("realEstate.casaMapa.admin.partnerDashboard.leadsHint")}</span>
        </Link>
        <Link className="partner-quick-card" to={path("profile")}>
          <span className="partner-quick-card__emoji">✨</span>
          <strong>{t("realEstate.casaMapa.admin.dashboard.profileHealth")}</strong>
          <span>
            {t("realEstate.casaMapa.admin.partnerDashboard.kpi.profileHint")} · {summary.profileCompleteness}%
          </span>
        </Link>
        <Link className="partner-quick-card" to={path("market")}>
          <span className="partner-quick-card__emoji">◐</span>
          <strong>{t("realEstate.casaMapa.admin.nav.market")}</strong>
          <span>{t("realEstate.casaMapa.admin.partnerMarket.intro")}</span>
        </Link>
          </>
        )}
      </section>

      <section className="partner-kpi-grid" aria-label={t("realEstate.casaMapa.admin.dashboard.kpiLabel")}>
        {isVendorPartner ? (
          <>
            <KpiCard
              icon="✓"
              label={t("realEstate.casaMapa.admin.partnerDirectory.kpi.published")}
              value={publishedVendorCount}
              tone="green"
            />
            <KpiCard
              icon="◌"
              label={t("realEstate.casaMapa.admin.partnerDirectory.kpi.draft")}
              value={draftVendorCount}
              tone="amber"
            />
            <KpiCard
              icon="🆕"
              label={t("realEstate.casaMapa.admin.partnerDashboard.kpi.newLeads")}
              value={summary.newLeads}
              hint={t("realEstate.casaMapa.admin.partnerDashboard.kpi.newLeadsHint")}
              tone="violet"
            />
            <KpiCard
              icon="✅"
              label={t("realEstate.casaMapa.admin.partnerDashboard.kpi.profile")}
              value={`${summary.profileCompleteness}%`}
              hint={t("realEstate.casaMapa.admin.partnerDashboard.kpi.profileHint")}
              tone="blue"
            />
          </>
        ) : (
          <>
            <KpiCard
              icon="🏠"
              label={t("realEstate.casaMapa.admin.partnerDashboard.kpi.activeListings")}
              value={(inventory?.totalListings ?? 0).toLocaleString()}
              hint={
                inventory?.totalListings
                  ? t("realEstate.casaMapa.admin.partnerDashboard.kpi.noInventoryHint")
                  : undefined
              }
              tone="blue"
            />
            <KpiCard
              icon="🆕"
              label={t("realEstate.casaMapa.admin.partnerDashboard.kpi.newLeads")}
              value={summary.newLeads}
              hint={t("realEstate.casaMapa.admin.partnerDashboard.kpi.newLeadsHint")}
              tone="violet"
            />
            <KpiCard
              icon="📬"
              label={t("realEstate.casaMapa.admin.partnerDashboard.kpi.assignedLeads")}
              value={summary.assignedLeads}
              hint={t("realEstate.casaMapa.admin.partnerDashboard.kpi.assignedLeadsHint")}
              tone="green"
            />
            <KpiCard
              icon="✅"
              label={t("realEstate.casaMapa.admin.partnerDashboard.kpi.profile")}
              value={`${summary.profileCompleteness}%`}
              hint={t("realEstate.casaMapa.admin.partnerDashboard.kpi.profileHint")}
              tone="amber"
            />
          </>
        )}
      </section>

      {!isVendorPartner && performanceTotals ? (
        <section className="partner-panel cm-dashboard__performance" aria-labelledby="cm-perf-title">
          <div className="partner-panel__header">
            <div>
              <h3 id="cm-perf-title">
                {t("realEstate.casaMapa.admin.partnerDashboard.performance.title", {
                  days: performance?.windowDays || 30,
                })}
              </h3>
              <p className="muted">
                {t("realEstate.casaMapa.admin.partnerDashboard.performance.hint")}
              </p>
            </div>
            <Link className="partner-dashboard-btn" to={path("leads")}>
              {t("realEstate.casaMapa.admin.nav.leads")}
            </Link>
          </div>
          <div className="partner-kpi-grid cm-dashboard__performance-kpis">
            <KpiCard
              icon="◎"
              label={t("realEstate.casaMapa.admin.partnerDashboard.performance.profileViews")}
              value={(performanceTotals.profileViews ?? 0).toLocaleString()}
              hint={t("realEstate.casaMapa.admin.partnerDashboard.performance.profileViewsHint")}
              tone="blue"
            />
            <KpiCard
              icon="◈"
              label={t("realEstate.casaMapa.admin.partnerDashboard.performance.listingImpressions")}
              value={(performanceTotals.listingImpressions ?? 0).toLocaleString()}
              hint={t(
                "realEstate.casaMapa.admin.partnerDashboard.performance.listingImpressionsHint"
              )}
              tone="green"
            />
            <KpiCard
              icon="✉"
              label={t("realEstate.casaMapa.admin.partnerDashboard.performance.inquiries")}
              value={(performanceTotals.inquiries ?? 0).toLocaleString()}
              hint={t("realEstate.casaMapa.admin.partnerDashboard.performance.inquiriesHint")}
              tone="violet"
            />
          </div>
          {performance?.topListings?.length ? (
            <div className="cm-dashboard__top-listings">
              <h4>{t("realEstate.casaMapa.admin.partnerDashboard.performance.topListings")}</h4>
              <ul>
                {performance.topListings.slice(0, 5).map((listing) => (
                  <li key={listing.listingId}>
                    <Link to={path(`listings/${listing.listingId}`)}>{listing.title}</Link>
                    <span>{(listing.views ?? 0).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="muted cm-dashboard__performance-empty">
              {t("realEstate.casaMapa.admin.partnerDashboard.performance.topListingsEmpty")}
            </p>
          )}
        </section>
      ) : null}

      <section className="partner-panel cm-partner-dashboard-tools">
        <div className="partner-panel__header">
          <div>
            <h3>{t("realEstate.casaMapa.admin.partnerDashboard.toolsSectionTitle")}</h3>
            <p className="muted">{t("realEstate.casaMapa.admin.partnerDashboard.toolsSectionHint")}</p>
          </div>
          <Link className="partner-dashboard-btn" to={path("market")}>
            {t("realEstate.casaMapa.admin.partnerDashboard.viewTools")}
          </Link>
        </div>
        <div className="cm-partner-dashboard-tools__grid">
          {CASAMAPA_PARTNER_FREE_TOOL_KEYS.slice(0, 4).map((toolKey) => {
            const meta = CASAMAPA_PARTNER_FREE_TOOLS[toolKey];
            const destination = meta?.portalSegment
              ? path(meta.portalSegment)
              : path("market");
            return (
              <Link key={toolKey} className="cm-partner-dashboard-tools__link" to={destination}>
                <strong>{t(`realEstate.casaMapa.advertise.freeTools.items.${toolKey}.name`)}</strong>
                <span>{t(`realEstate.casaMapa.advertise.freeTools.items.${toolKey}.outcome`)}</span>
              </Link>
            );
          })}
        </div>
      </section>

      <div className="cm-dashboard__main">
        <div className="cm-dashboard__primary">
          {!isVendorPartner && inventory && (inventory.saleListings > 0 || inventory.rentListings > 0) ? (
            <section className="partner-panel cm-dashboard__snapshot">
              <div className="partner-panel__header">
                <div>
                  <h3>{t("realEstate.casaMapa.admin.dashboard.marketplaceSnapshot")}</h3>
                  <p className="muted">{t("realEstate.casaMapa.admin.dashboard.inventoryMixHint")}</p>
                </div>
                <Link className="partner-dashboard-btn" to={path("listings")}>
                  {t("realEstate.casaMapa.admin.nav.listings")}
                </Link>
              </div>
              <div className="cm-dashboard__snapshot-stats">
                <div>
                  <span className="cm-dashboard__snapshot-label">
                    {t("realEstate.casaMapa.admin.listingType.sale")}
                  </span>
                  <strong>{(inventory.saleListings ?? 0).toLocaleString()}</strong>
                </div>
                <div>
                  <span className="cm-dashboard__snapshot-label">
                    {t("realEstate.casaMapa.admin.listingType.rent")}
                  </span>
                  <strong>{(inventory.rentListings ?? 0).toLocaleString()}</strong>
                </div>
                <div>
                  <span className="cm-dashboard__snapshot-label">
                    {t("realEstate.casaMapa.admin.dashboard.totalListings")}
                  </span>
                  <strong>{(inventory.totalListings ?? 0).toLocaleString()}</strong>
                </div>
              </div>
              <div className="cm-dashboard__mix cm-dashboard__mix--large">
                <div className="cm-dashboard__mix-labels">
                  <span>
                    {t("realEstate.casaMapa.admin.listingType.sale")} {salePct}%
                  </span>
                  <span>
                    {t("realEstate.casaMapa.admin.listingType.rent")} {100 - salePct}%
                  </span>
                </div>
                <div className="cm-dashboard__mix-track">
                  <div className="cm-dashboard__mix-sale" style={{ width: `${salePct}%` }} />
                  <div className="cm-dashboard__mix-rent" style={{ width: `${100 - salePct}%` }} />
                </div>
              </div>
            </section>
          ) : null}

          <section className="partner-panel">
            <div className="partner-panel__header">
              <div>
                <h3>
                  {isVendorPartner
                    ? t("realEstate.casaMapa.admin.partnerDashboard.vendorProfiles")
                    : t("realEstate.casaMapa.admin.partnerDashboard.yourAgencies")}
                </h3>
                <p className="muted">
                  {isVendorPartner
                    ? t("realEstate.casaMapa.admin.partnerDashboard.vendorProfilesHint")
                    : t("realEstate.casaMapa.admin.partnerDashboard.yourAgenciesHint")}
                </p>
              </div>
              {isVendorPartner ? (
                <Link className="partner-dashboard-btn" to={path("directory")}>
                  {t("realEstate.casaMapa.admin.partnerDashboard.viewDirectory")}
                </Link>
              ) : agencies.length > 1 ? (
                <Link className="partner-dashboard-btn" to={path("agencies")}>
                  {t("realEstate.casaMapa.admin.dashboard.viewAllAgencies")}
                </Link>
              ) : null}
            </div>

            {isVendorPartner ? (
              vendorProfiles.length ? (
                <ul className="cm-dashboard__attention">
                  {vendorProfiles.slice(0, 4).map((vendor) => (
                    <li key={vendor.id} className="cm-dashboard__attention-item cm-dashboard__attention-item--neutral">
                      <span>
                        {vendor.name} ·{" "}
                        {t(
                          `realEstate.casaMapa.admin.vendors.publicationStatus.${vendor.publicationStatus || "draft"}`,
                          {},
                          vendor.publicationStatus || "draft"
                        )}
                      </span>
                      <Link to={path("directory")}>
                        {t("realEstate.casaMapa.admin.partnerDashboard.actions.open")}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="cm-partner-empty-state">
                  <p className="muted">{t("realEstate.casaMapa.admin.partnerDashboard.noVendorProfiles")}</p>
                  <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("profile")}>
                    {t("realEstate.casaMapa.admin.partnerDashboard.manageProfile")}
                  </Link>
                </div>
              )
            ) : agencies.length ? (
              <div className="cm-dashboard__agency-list">
                {agencies.slice(0, 4).map((agency) => (
                  <AgencyWorkspaceCard key={agency.slug} agency={agency} t={t} formatMoney={formatMoney} />
                ))}
              </div>
            ) : (
              <div className="cm-partner-empty-state">
                <p className="muted">{t("realEstate.casaMapa.admin.partnerDashboard.noAgencies")}</p>
                <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("profile")}>
                  {t("realEstate.casaMapa.admin.partnerDashboard.manageProfile")}
                </Link>
              </div>
            )}
          </section>
        </div>

        <aside className="cm-dashboard__sidebar">
          <section className="partner-panel">
            <h3>{t("realEstate.casaMapa.admin.partnerDashboard.leadPipeline")}</h3>
            <PipelineBar
              label={t("realEstate.casaMapa.admin.partnerDashboard.pipeline.new")}
              value={summary.newLeads}
              max={leadMax}
              tone="green"
            />
            <PipelineBar
              label={t("realEstate.casaMapa.admin.partnerDashboard.pipeline.inProgress")}
              value={summary.contactedLeads}
              max={leadMax}
              tone="blue"
            />
            <PipelineBar
              label={t("realEstate.casaMapa.admin.partnerDashboard.pipeline.closed")}
              value={summary.closedLeads}
              max={leadMax}
              tone="slate"
            />
            <Link className="cm-dashboard__panel-link" to={path("leads")}>
              {t("realEstate.casaMapa.admin.partnerDashboard.viewLeads")} →
            </Link>
          </section>

          <section className="partner-panel">
            <div className="partner-panel__header">
              <h3>{t("realEstate.casaMapa.admin.partnerDashboard.needsAttention")}</h3>
            </div>
            {attentionItems.length ? (
              <ul className="cm-dashboard__attention">
                {attentionItems.map((item) => (
                  <li
                    key={item.label}
                    className={`cm-dashboard__attention-item cm-dashboard__attention-item--${item.tone}`}
                  >
                    <span>{item.label}</span>
                    <Link to={item.to}>{item.action}</Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t("realEstate.casaMapa.admin.partnerDashboard.allCaughtUp")}</p>
            )}
          </section>

          <section className="partner-panel">
            <div className="partner-panel__header">
              <h3>{t("realEstate.casaMapa.admin.dashboard.profileHealth")}</h3>
              <span>{summary.profileCompleteness}%</span>
            </div>
            <div className="partner-progress">
              <div className="partner-progress__track" aria-hidden="true">
                <div
                  className="partner-progress__fill"
                  style={{ width: `${Math.min(100, summary.profileCompleteness)}%` }}
                />
              </div>
            </div>
            <p className="muted cm-partner-profile-hint">
              {t("realEstate.casaMapa.admin.partnerDashboard.profileHint")}
            </p>
            <Link className="cm-dashboard__panel-link" to={path("profile")}>
              {t("realEstate.casaMapa.admin.partnerDashboard.manageProfile")} →
            </Link>
          </section>
        </aside>
      </div>
    </div>
  );
}
